import { ScanLineIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { DOCUMENT_KIND_HREF } from "@/features/documents/document-kind"
import { ScanDialog } from "@/features/scan/ScanDialog"
import { useFindDocument } from "@/features/scan/use-find-document"
import { Button } from "@/components/ui/button"

/**
 * "Scan a document" — the button, the dialog and what happens after (§9).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS FOR. Every bill, quotation and delivery challan this app prints
 * carries a Code 128 barcode of its number and a QR. A pile of paper on the desk
 * becomes searchable: point a handheld scanner at a sheet and the document opens.
 * The challan is the one that physically travels, so it is the sheet most likely
 * to come back creased and be scanned rather than typed.
 *
 * WHAT HAPPENS WHEN NOTHING MATCHES is the part worth getting right. The owner
 * has just scanned a real piece of paper, so "not found" is surprising and needs
 * to say WHAT was read — the number is right there in the message, which is how
 * he sees that the scanner picked up a neighbouring label, or that the document
 * predates the app and was never entered.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ScanAction({
  render,
}: {
  /** Supply a custom trigger — the sidebar passes its own menu button. */
  render?: (open: () => void) => React.ReactNode
}): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const find = useFindDocument()

  const handleScan = (value: string) => {
    find.mutate(value, {
      onSuccess: (found) => {
        /*
         * ONE BRANCH — "found something" or "did not" — and the destination
         * comes from `DOCUMENT_KIND_HREF`, keyed by kind.
         *
         * This was an `if` per kind: a ternary in disguise, and not exhaustive.
         * When challans shipped there was no third `if`, so a scanned
         * `CHL/26-27/7` fell through to the red "Nothing found" toast — about a
         * barcode this app had printed on the sheet in his hand. Keying the
         * navigation means a new document kind cannot fall through; it fails to
         * compile in `features/documents/document-kind.tsx` instead.
         */
        if (found.kind !== "none") {
          void navigate(DOCUMENT_KIND_HREF[found.kind](found.id))
          return
        }
        // Naming what was read is what makes this actionable rather than a dead
        // end — see the note above.
        toast.error(t("scan.notFound", { value }))
      },
      onError: () => {
        toast.error(t("err.somethingWentWrong"))
      },
    })
  }

  return (
    <>
      {render === undefined ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setOpen(true)
          }}
        >
          <ScanLineIcon />
          {t("scan.action")}
        </Button>
      ) : (
        render(() => {
          setOpen(true)
        })
      )}

      <ScanDialog open={open} onOpenChange={setOpen} onScan={handleScan} />
    </>
  )
}
